import React, { useCallback, useMemo, type FC } from 'react'

import { useAtom } from 'jotai'
import {
  Flex,
  FormElement,
  Select,
  SELECT_VARIANT_WITH_CHECKBOX,
  Toggle,
} from 'mailup-web-components'
import { useIntl } from 'react-intl'

import { MAX_SELECT_ITEMS } from '../../../config/config'

import useAsyncSelect from 'Hooks/useAsyncSelect'

import { toggleAllCategoriesCallback } from 'Pages/unifiedFiltersListPage/callbacks/search'
import {
  fetchCategoriesByName,
  fetchCategoryById,
} from 'Pages/unifiedFiltersListPage/services/search'
import { filtersQueryAtom } from 'Pages/unifiedFiltersListPage/state/state'
import { useAtomCallback } from 'jotai/utils'

const LabelRightComponent: FC = () => {
  const intl = useIntl()

  const [filtersQuery] = useAtom(filtersQueryAtom)

  const handleToggle = useAtomCallback(
    useCallback(toggleAllCategoriesCallback, []),
  )

  return (
    <Flex justifyContent='end'>
      <Flex gap='8px' alignItems='center'>
        <Toggle
          data-mu-test='toggle-filter-types'
          variant='sm'
          checked={filtersQuery.allCategories}
          onChange={handleToggle}
        />
        <span>
          {intl.formatMessage({ id: 'Load.MailupCommon.BtnSelectAll' })}
        </span>
      </Flex>
    </Flex>
  )
}

const InputCategories: FC = () => {
  const intl = useIntl()

  const [{ categories, allCategories }, setFilters] = useAtom(filtersQueryAtom)

  const { options, loading, searching, handleSearch, hint, hintNoResults } =
    useAsyncSelect({
      key: 'filterCategories',
      value: categories,
      maxItems: MAX_SELECT_ITEMS,
      fetchItems: fetchCategoriesByName,
      fetchItemsByTerm: fetchCategoriesByName,
      fetchItemById: fetchCategoryById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const handleExpandCollapse = (c: boolean): void => {
    if (!c) {
      handleSearch('')
    }
  }

  const handleChange = (v: number[]): void => {
    setFilters((prev) => ({ ...prev, categories: v }))
  }

  const optionsWithNoCategory = useMemo(() => {
    return searching
      ? options
      : [
          {
            value: 0,
            label: intl.formatMessage({ id: 'Load.Filters.NoCategory' }),
          },
        ].concat(options)
  }, [searching, options, intl])

  return (
    <FormElement
      label={intl.formatMessage({ id: 'Load.Common.Category' })}
      LabelRightComponent={LabelRightComponent}
    >
      <Select
        data-mu-test='input-categories'
        value={categories}
        options={optionsWithNoCategory}
        loading={loading}
        onChange={handleChange}
        onSearch={handleSearch}
        onExpandCollapse={handleExpandCollapse}
        multiple
        variant={SELECT_VARIANT_WITH_CHECKBOX}
        hint={hint}
        hintNoResults={hintNoResults}
        memoSelectOption
        placeholder={intl.formatMessage({
          id: 'Load.MailupCommon.Select',
        })}
        disabled={allCategories}
      />
    </FormElement>
  )
}

export default InputCategories
